import React, { useEffect, useState } from 'react';
import './PayerNodePicker.css';

const API_URL = process.env.REACT_APP_API_URL || '';

// The payer family tree (GET /api/rules/payer-nodes) as one grouped <select>:
// each family is an <optgroup> holding itself plus its lob/plan descendants,
// so "UnitedHealthcare" and "UnitedHealthcare › WellMed" sit together. The rules
// engine scopes on payer.family / lob / plan through this link, so a practice
// payer left unlinked matches no payer-scoped rule.
//
// The endpoint is admin-only. A 403 hides the picker rather than showing an
// empty control; the existing link (if any) is still displayed read-only.

export function buildGroups(nodes) {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const label = (n) => {
    const chain = [];
    let cur = n;
    const seen = new Set();
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      chain.unshift(cur.name);
      cur = cur.parent_id ? byId.get(cur.parent_id) : null;
    }
    return chain.join(' › ');
  };
  const rootOf = (n) => {
    let cur = n;
    const seen = new Set();
    while (cur && cur.parent_id && byId.has(cur.parent_id) && !seen.has(cur.id)) {
      seen.add(cur.id);
      cur = byId.get(cur.parent_id);
    }
    return cur;
  };
  const groups = new Map();
  nodes.forEach((n) => {
    const root = rootOf(n);
    if (!groups.has(root.id)) groups.set(root.id, { root, options: [] });
    groups.get(root.id).options.push({ id: n.id, label: label(n), level: n.level });
  });
  const sorted = Array.from(groups.values()).sort((a, b) => a.root.name.localeCompare(b.root.name));
  sorted.forEach((g) => g.options.sort((a, b) => a.label.localeCompare(b.label)));
  return sorted;
}

function PayerNodePicker({ value, onChange, current }) {
  const [nodes, setNodes] = useState(null); // null = loading, [] = none, false = not allowed
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_URL}/api/rules/payer-nodes`, { credentials: 'include' });
        if (response.status === 403 || response.status === 401) {
          if (!cancelled) setNodes(false);
          return;
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (!cancelled) setNodes(data.payer_nodes || []);
      } catch (err) {
        if (!cancelled) { setNodes([]); setError('Could not load the payer tree'); }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (nodes === false) {
    return current ? (
      <div className="form-field payer-node-picker">
        <label>Payer family</label>
        <div className="payer-node-current">{current.label}</div>
      </div>
    ) : null;
  }

  return (
    <div className="form-field payer-node-picker">
      <label>Payer family</label>
      <select
        value={value == null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        disabled={nodes === null}
        aria-label="Payer family"
      >
        <option value="">— not linked —</option>
        {nodes && buildGroups(nodes).map((g) => (
          <optgroup key={g.root.id} label={g.root.name}>
            {g.options.map((o) => (
              <option key={o.id} value={String(o.id)}>{o.label}</option>
            ))}
          </optgroup>
        ))}
      </select>
      <div className="payer-node-hint">
        {error
          ? <span className="payer-node-error">{error}</span>
          : 'Links this payer to the rules engine’s payer tree (family › plan). Unlinked payers match no payer-scoped rule.'}
      </div>
    </div>
  );
}

export default PayerNodePicker;
